import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// @ts-ignore-next-line importsNotUsedAsValues
import { useRef, useState } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/fp/isEqual';
import isEmpty from 'lodash/fp/isEmpty';
import noop from 'lodash/fp/noop';
import BaseSelectDropdown, { filterOptions } from './BaseSelectDropdown';
import MultiselectToggleCounter from './MultiselectToggleCounter';
import MultiselectToggleSelection from './MultiselectToggleSelection';
import MultiselectToggleFilter from './MultiselectToggleFilter';
import MultiselectTogglePlaceholder from './MultiselectTogglePlaceholder';
import useClickOutside from '../../useClickOutside';
import useMergeRefs from '../../hooks/useMergeRefs';
import WithFeedbackAndAddon from './WithFeedbackAndAddon';
const DEFAULT_FOCUSED_ITEM_INDEX = -1;
const Multiselect = (props) => {
    const { name, id = name, options = [], value, onChange = noop, placeholder, dropup = false, pullRight = false, autoDropDirection = true, bsSize = 'md', disabled = false, tabIndex = 0, hasError = false, useFilter = false, multiline = false, noItemMessage, counterMessage, renderCounterMessage, showSelectedItemIcon = false, showUnselectedItemIcons = false, className, inputAddon, errorMessage, warningMessage, messageWhiteSpace = 'normal' } = props, remainingProps = __rest(props, ["name", "id", "options", "value", "onChange", "placeholder", "dropup", "pullRight", "autoDropDirection", "bsSize", "disabled", "tabIndex", "hasError", "useFilter", "multiline", "noItemMessage", "counterMessage", "renderCounterMessage", "showSelectedItemIcon", "showUnselectedItemIcons", "className", "inputAddon", "errorMessage", "warningMessage", "messageWhiteSpace"]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState(value || []);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [itemDOMValues, setItemDOMValues] = useState([]);
    const [focusedItemIndex, setFocusedItemIndex] = useState(DEFAULT_FOCUSED_ITEM_INDEX);
    const [requestItemDOMValues, setRequestItemDOMValues] = useState(false);
    const [keyboardUsed, setKeyboardUsed] = useState(false);
    const refToggle = useRef(null);
    const refMultiSelectWrapper = useRef(null);
    const ref = useClickOutside(() => closeMenu());
    const mergedSelectRefs = useMergeRefs(refMultiSelectWrapper, ref);
    const updateSelectedItems = (optionsToCheck, selectedItem) => {
        if (selectedItem) {
            setSelectedItemIds(selectedItem);
        }
        else if (optionsToCheck) {
            const filteredBySelection = optionsToCheck.filter(item => item.selected);
            setSelectedItemIds(filteredBySelection.map(option => option.id));
        }
    };
    // Update the selectedItems on first render.
    // Note, we use a ref instead of an effect as the effect is triggered too late
    const firstRenderRef = useRef(true);
    if (firstRenderRef.current) {
        updateSelectedItems(options, value);
        firstRenderRef.current = false;
    }
    // Update selection based on changed value prop
    const [previousValue, setPreviousValue] = useState(value);
    if (!isEqual(value, previousValue)) {
        updateSelectedItems(options, value);
        setPreviousValue(value);
    }
    // Update options when they have changed from the outside
    const [previousOptions, setPreviousOptions] = useState(options);
    if (!isEqual(options, previousOptions)) {
        updateSelectedItems(options, value);
        setFilteredOptions(options);
        setRequestItemDOMValues(true);
        setPreviousOptions(options);
    }
    const updateDOMValues = (updatedItemDOMValues = []) => {
        setItemDOMValues(updatedItemDOMValues);
        setRequestItemDOMValues(false);
    };
    const renderFilter = () => (_jsx(MultiselectToggleFilter, { isActive: isFilterActive || !!filterValue, selectedItemIds: selectedItemIds, filterValue: filterValue, onFilterChange: handleFilterChange, multiline: multiline }));
    const renderSelection = () => {
        if (counterMessage || renderCounterMessage) {
            return (_jsx(MultiselectToggleCounter, { selectedAmount: selectedItemIds.length, counterMessage: counterMessage, customRenderer: renderCounterMessage }));
        }
        return (_jsx(MultiselectToggleSelection, Object.assign({ options: options, selectedItemIds: selectedItemIds, showSelectedItemIcon: showSelectedItemIcon, showUnselectedItemIcons: showUnselectedItemIcons, onRemoveItem: handleRemoveSelectedItem, multiline: multiline, hasInputAddon: !!inputAddon, hasFilter: useFilter }, { children: multiline && renderFilter() })));
    };
    const renderToggle = () => {
        const wrapperClassNames = classNames('dropdown-toggle', 'form-control', 'text-left', bsSize === 'sm' && 'input-sm', bsSize === 'lg' && 'input-lg', multiline && 'height-auto', disabled && 'disabled');
        const showPlaceholder = isEmpty(selectedItemIds) && !showUnselectedItemIcons;
        const showFilter = useFilter && isOpen && !counterMessage && !renderCounterMessage;
        const toggleButton = (_jsxs("button", Object.assign({ type: 'button', id: id, name: name, className: wrapperClassNames, "data-toggle": 'dropdown', tabIndex: tabIndex, "aria-haspopup": 'true', "aria-expanded": isOpen, onClick: onToggle, onKeyDown: handleToggleKeyDown, ref: refToggle }, { children: [showPlaceholder ? (_jsxs(_Fragment, { children: [_jsx(MultiselectTogglePlaceholder, { placeholder: placeholder }), showFilter && renderFilter()] })) : (renderSelection()), showFilter && !multiline && renderFilter(), _jsx("span", { className: 'caret' })] })));
        if (!inputAddon && !errorMessage && !warningMessage) {
            return toggleButton;
        }
        return (_jsx(WithFeedbackAndAddon, Object.assign({ bsSize: bsSize, inputAddon: inputAddon, errorMessage: errorMessage, warningMessage: warningMessage, messageWhiteSpace: messageWhiteSpace }, { children: toggleButton })));
    };
    const renderDropdownMenu = () => {
        const nonSelectedOptions = filteredOptions.map(option => {
            option.selected = selectedItemIds.indexOf(option.id) !== -1;
            return option;
        });
        return (_jsx(BaseSelectDropdown, { isOpen: isOpen, options: nonSelectedOptions, focusedItemIndex: focusedItemIndex, keyboardUsed: keyboardUsed, updateDOMValues: updateDOMValues, onSelect: onOptionChange, onClose: closeMenu, noItemMessage: noItemMessage, pullRight: pullRight, useActiveClass: true, dropup: dropup, autoDropDirection: autoDropDirection, requestItemDOMValues: requestItemDOMValues }));
    };
    const handleFilterChange = (event) => {
        event.preventDefault();
        const targetFilterValue = event.currentTarget.value;
        const optionsFiltered = filterOptions(itemDOMValues, targetFilterValue, options);
        // highlight the first item of the search result if at least one item was found
        const newFocusedItemIndex = optionsFiltered.length > 0 ? 0 : -1;
        setIsFilterActive(true);
        setFilterValue(targetFilterValue);
        setFilteredOptions(optionsFiltered);
        setKeyboardUsed(true);
        setFocusedItemIndex(newFocusedItemIndex);
    };
    const onOptionChange = (currentSelectedItem) => {
        // prevent selecting disabled Items via keyboard
        if (currentSelectedItem === null || currentSelectedItem === void 0 ? void 0 : currentSelectedItem.disabled) {
            return;
        }
        const updatedSelectedItems = currentSelectedItem ? updateSelection(currentSelectedItem.id) : selectedItemIds;
        setSelectedItemIds(updatedSelectedItems);
        setIsFilterActive(false);
        setFilterValue('');
        setFilteredOptions(options);
        onChange(updatedSelectedItems);
    };
    const updateSelection = (selectedItemId) => {
        const newSelectedItems = [...selectedItemIds];
        const itemIndex = selectedItemIds.indexOf(selectedItemId);
        if (itemIndex !== -1) {
            newSelectedItems.splice(itemIndex, 1);
        }
        else {
            newSelectedItems.push(selectedItemId);
        }
        return newSelectedItems;
    };
    const handleRemoveSelectedItem = (event, itemId) => {
        event.stopPropagation();
        const newSelectedItems = updateSelection(itemId);
        setSelectedItemIds(newSelectedItems);
        onChange(newSelectedItems);
    };
    const handleToggleKeyDown = (event) => {
        switch (event.key) {
            case 'Space':
                if (!isOpen) {
                    // open on space
                    event.preventDefault();
                    onToggle(event);
                }
                break;
            case 'Enter':
                if (!isOpen) {
                    // open on enter
                    event.preventDefault();
                    onToggle(event);
                }
                break;
            default:
                break;
        }
    };
    const onToggle = (event) => {
        // Don't toggle when component is disabled or an item in the toggle was clicked in order to remove from
        // selection neither close when filter is active, means entering some filter value
        // in order to avoid closing menu on space but allow to use it for filtering
        if (disabled || isFilterActive) {
            return;
        }
        // using the enter key on the toggle button will trigger a synthetic click event as all buttons are of
        // type submit by default in HTML. In order to differentiate between real click and a synthetic event
        // caused by they keyboard, use the event details. A synthetic event is always 0.
        const isKeyboardUsed = event.detail === 0;
        setIsOpen(!isOpen);
        setKeyboardUsed(isKeyboardUsed);
    };
    const closeMenu = () => {
        var _a;
        if (isOpen) {
            setIsOpen(false);
            setIsFilterActive(false);
            setKeyboardUsed(false);
            (_a = refToggle === null || refToggle === void 0 ? void 0 : refToggle.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const wrapperClasses = classNames('select', 'multiselect', 'dropdown', isOpen && 'open', hasError && 'has-error', className && className);
    return (_jsxs("div", Object.assign({}, remainingProps, { className: wrapperClasses, ref: mergedSelectRefs }, { children: [renderToggle(), renderDropdownMenu()] })));
};
export default Multiselect;
